import { useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Turnstile, TurnstileRef } from './Turnstile';
import { Mail, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { useAnalytics } from '../../hooks/useAnalytics';

const API_BASE = import.meta.env.VITE_AI_WORKER_URL || '';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

// Official Google "G" multi-color icon
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

interface SignupFormProps {
  onSwitchToLogin: () => void;
}

// Helper to format plan name
function formatPlanName(plan: string): string {
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

export function SignupForm({ onSwitchToLogin }: SignupFormProps) {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileRef>(null);
  const { signUp, signInWithGoogle, resendConfirmationEmail } = useAuth();
  const analytics = useAnalytics();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the verification');
      return;
    }

    setLoading(true);

    try {
      // Verify Turnstile token with backend (if configured)
      if (TURNSTILE_SITE_KEY && turnstileToken) {
        const verifyResponse = await fetch(`${API_BASE}/auth/verify-turnstile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: turnstileToken }),
        });

        if (!verifyResponse.ok) {
          const verifyData = await verifyResponse.json().catch(() => ({}));
          setError((verifyData as { error?: string }).error || 'Verification failed. Please try again.');
          setTurnstileToken(null);
          turnstileRef.current?.reset();
          setLoading(false);
          return;
        }
      }

      // Sign up the user
      const { error, data } = await signUp(email, password, fullName);

      console.log('[SignupForm] Signup response:', { error, data, userId: data?.user?.id });

      if (error) {
        setError(error.message);
        setTurnstileToken(null);
        turnstileRef.current?.reset();
        setLoading(false);
        return;
      }

    // If successful and there's a plan/interval, store in database
    const plan = searchParams.get('plan');
    const interval = searchParams.get('interval');

    console.log('[SignupForm] URL params:', { plan, interval });
    console.log('[SignupForm] Conditions check:', {
      hasUserId: !!data?.user?.id,
      hasPlan: !!plan,
      hasInterval: !!interval,
      willInsert: !!(data?.user?.id && plan && interval)
    });

    if (data?.user?.id && plan && interval) {
      console.log('[SignupForm] Attempting to create pending subscription...');
      // Call the database function to create pending subscription
      // This uses SECURITY DEFINER to bypass RLS
      const { error: insertError } = await supabase.rpc('create_pending_subscription', {
        p_user_id: data.user.id,
        p_plan: plan,
        p_interval: interval,
      });
      if (insertError) {
        console.error('[SignupForm] Failed to create pending subscription:', insertError);
      } else {
        console.log('[SignupForm] Created pending subscription for user:', data.user.id);
      }
    } else {
      console.log('[SignupForm] Skipping pending subscription - missing required data');
    }

    // Track signup event for analytics
    analytics.signUp({
      method: 'email',
      userId: data?.user?.id,
    });

    setSignupSuccess(true);
    } catch {
      setError('An error occurred. Please try again.');
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setError('');
    setLoading(true);
    const { error } = await resendConfirmationEmail(email);
    
    if (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      const plan = searchParams.get('plan');
      const interval = searchParams.get('interval');

      // Track Google signup attempt (actual completion tracked after OAuth redirect)
      // Note: We track here since the user will be redirected to Google
      analytics.signUp({ method: 'google' });

      // For Google OAuth, we can use direct redirect since OAuth handles it differently
      // Google OAuth maintains the same browser session unlike email confirmation
      if (plan && interval) {
        const redirectUrl = `/virtual-staging/pricing?plan=${plan}&interval=${interval}`;
        const { error } = await signInWithGoogle(redirectUrl);
        if (error) {
          setError(error.message);
        }
      } else {
        const { error } = await signInWithGoogle();
        if (error) {
          setError(error.message);
        }
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  // Show success message after signup
  if (signupSuccess) {
    const plan = searchParams.get('plan');
    const interval = searchParams.get('interval');
    const isSubscribing = plan && interval;

    return (
      <div className="text-center space-y-6">
        <div className="flex justify-center">
          <div className="bg-olive/10 p-5 rounded-full border border-olive/20">
            <CheckCircle className="w-12 h-12 text-olive" />
          </div>
        </div>

        <h2 className="text-3xl md:text-4xl font-serif font-light tracking-tight text-charcoal">
          Check Your Email
        </h2>

        <div className="space-y-4">
          <p className="text-lg text-sand font-light">
            We've sent a confirmation email to:
          </p>
          <p className="font-medium text-charcoal bg-alabaster px-6 py-3 rounded-full border border-stone inline-block">
            {email}
          </p>
        </div>

        {isSubscribing && (
          <div className="bg-olive/10 border border-olive/20 rounded-3xl p-6 text-center">
            <p className="text-charcoal font-medium mb-2">
              {formatPlanName(plan)} Plan Selected
            </p>
            <p className="text-sm text-sand font-light">
              After confirming your email, you'll be redirected to complete your subscription.
            </p>
          </div>
        )}

        <div className="bg-alabaster border border-stone rounded-3xl p-6 space-y-3 text-left">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-olive flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium mb-2 text-charcoal">Next Steps:</p>
              <ol className="list-decimal list-inside space-y-1.5 text-sand font-light">
                <li>Open the confirmation email</li>
                <li>Click the confirmation link</li>
                {isSubscribing ? (
                  <li>Complete your subscription to start staging!</li>
                ) : (
                  <li>Sign in to start staging properties!</li>
                )}
              </ol>
            </div>
          </div>
        </div>

        <div className="pt-4 space-y-4">
          <p className="text-sm text-sand font-light">
            Didn't receive the email?
          </p>
          <button
            onClick={handleResendConfirmation}
            disabled={loading}
            className="w-full bg-olive hover:bg-charcoal text-alabaster py-4 rounded-full font-medium transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-soft hover:shadow-lift hover:-translate-y-0.5 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-alabaster border-t-transparent rounded-full animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Mail className="w-5 h-5" />
                <span>Resend Confirmation Email</span>
              </>
            )}
          </button>
        </div>

        <div className="pt-6 border-t border-stone">
          <button
            onClick={onSwitchToLogin}
            className="text-sand hover:text-terracotta font-light transition-colors duration-300 text-sm"
          >
            Back to Sign In
          </button>
        </div>

        {error && (
          <div className="bg-terracotta/10 border border-terracotta/30 text-terracotta px-4 py-3 rounded-2xl text-sm font-light flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Google Sign-Up Button */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={googleLoading || loading}
          className="w-full bg-white hover:bg-alabaster border border-stone text-charcoal py-4 rounded-full font-medium transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-soft hover:shadow-lift hover:-translate-y-0.5 flex items-center justify-center gap-3"
        >
          {googleLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-charcoal border-t-transparent rounded-full animate-spin" />
              <span>Connecting...</span>
            </>
          ) : (
            <>
              <GoogleIcon className="w-5 h-5" />
              <span>Continue with Google</span>
            </>
          )}
        </button>
        <p className="text-xs text-center text-antique font-light">
          By continuing, you agree to our{' '}
          <Link to="/terms" target="_blank" className="text-sand hover:text-terracotta underline underline-offset-2 transition-colors">
            Terms
          </Link>{' '}
          and{' '}
          <Link to="/privacy" target="_blank" className="text-sand hover:text-terracotta underline underline-offset-2 transition-colors">
            Privacy Policy
          </Link>
        </p>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-stone"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-sand font-light">or</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 mt-6">
        {error && (
          <div className="bg-terracotta/10 border border-terracotta/30 text-terracotta px-4 py-3 rounded-2xl text-sm font-light flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div>
          <label htmlFor="fullName" className="block text-xs font-mono uppercase tracking-widest text-sand mb-3">
            Full Name
          </label>
          <input
            id="fullName"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            className="w-full px-5 py-3.5 bg-alabaster border border-stone rounded-full text-charcoal placeholder:text-antique focus:outline-none focus:border-olive focus:ring-2 focus:ring-olive/10 transition-all duration-300 font-light"
            placeholder="John Smith"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-xs font-mono uppercase tracking-widest text-sand mb-3">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-5 py-3.5 bg-alabaster border border-stone rounded-full text-charcoal placeholder:text-antique focus:outline-none focus:border-olive focus:ring-2 focus:ring-olive/10 transition-all duration-300 font-light"
            placeholder="you@company.com"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-xs font-mono uppercase tracking-widest text-sand mb-3">
            Password
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            className="w-full px-5 py-3.5 bg-alabaster border border-stone rounded-full text-charcoal placeholder:text-antique focus:outline-none focus:border-olive focus:ring-2 focus:ring-olive/10 transition-all duration-300 font-light"
            placeholder="••••••••"
          />
          <p className="text-xs text-antique mt-2 ml-5 font-light">Minimum 6 characters</p>
        </div>

        {/* Terms & Privacy Consent */}
        <div className="flex items-start gap-3">
          <input
            id="terms"
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-stone text-olive focus:ring-olive/20 focus:ring-2 cursor-pointer"
          />
          <label htmlFor="terms" className="text-sm text-sand font-light leading-relaxed cursor-pointer">
            I agree to the{' '}
            <Link to="/terms" target="_blank" className="text-charcoal hover:text-terracotta underline underline-offset-2 transition-colors">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" target="_blank" className="text-charcoal hover:text-terracotta underline underline-offset-2 transition-colors">
              Privacy Policy
            </Link>
          </label>
        </div>

        {/* Turnstile CAPTCHA */}
        {TURNSTILE_SITE_KEY && (
          <Turnstile
            ref={turnstileRef}
            siteKey={TURNSTILE_SITE_KEY}
            onSuccess={(token) => setTurnstileToken(token)}
            onError={() => setTurnstileToken(null)}
            onExpired={() => setTurnstileToken(null)}
          />
        )}

        <button
          type="submit"
          disabled={loading || !agreedToTerms || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
          className="w-full bg-olive hover:bg-charcoal text-alabaster py-4 rounded-full font-medium transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-soft hover:shadow-lift hover:-translate-y-0.5 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-alabaster border-t-transparent rounded-full animate-spin" />
              <span>Creating account...</span>
            </>
          ) : (
            <>
              <span>Create Account</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </form>

      <div className="mt-8 text-center border-t border-stone pt-6">
        <p className="text-sand font-light">
          Already have an account?{' '}
          <button
            onClick={onSwitchToLogin}
            className="text-charcoal hover:text-terracotta font-medium transition-colors duration-300"
          >
            Sign in
          </button>
        </p>
      </div>
    </>
  );
}
